<template>
    <vx-box id="marketing-rules" color="info">
        <message-standard-rules-header
            :rulesCount="marketingCount"
            v-model:enabled="marketingEnabled"
            v-model:expanded="showMarketing"
        >
            <template v-slot:title>Marketing Rules</template>
        </message-standard-rules-header>

        <animate-height :height="showMarketing ? 'auto' : 0" :animate-opacity="true">
            <message-standard-rules v-if="messageStandard.marketing_rules" v-model="messageStandard.marketing_rules" />
        </animate-height>
    </vx-box>

    <vx-box id="transactional-rules" color="info">
        <message-standard-rules-header
            :rulesCount="transactionalCount"
            v-model:enabled="transactionalEnabled"
            v-model:expanded="showTransactional"
        >
            <template v-slot:title>Transactional Rules</template>
        </message-standard-rules-header>
        <animate-height :height="showTransactional ? 'auto' : 0" :animate-opacity="true">
            <div
                class="flex flex-col gap-4 border-b p-4 lg:px-8 lg:py-6"
                :class="[messageStandard.transactional_config === null ? 'border-b-slate-200' : 'border-b-transparent']"
            >
                <vx-tabs v-model="transactionalConfigTab" size="lg" class="flex-col lg:flex-row">
                    <vx-tab value="block_all_sending">Block All Sending</vx-tab>
                    <vx-tab value="use_marketing_rules">Use Marketing Rules</vx-tab>
                    <vx-tab value="custom">Use Custom Rules</vx-tab>
                </vx-tabs>

                <vx-alert v-if="transactionalConfigTab === undefined" color="slate" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faDiamondExclamation" />
                    </template>
                    No transactional rules are set
                </vx-alert>
                <vx-alert v-if="transactionalConfigTab === 'block_all_sending'" color="info" :closeable="false">
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faDiamondExclamation" />
                    </template>
                    Sending not allowed for transactional
                </vx-alert>
                <vx-alert v-if="transactionalConfigTab === 'use_marketing_rules'" color="primary" :closeable="false">
                    <strong>{{ marketingCount }} Rules</strong> applied from marketing
                </vx-alert>
            </div>

            <message-standard-rules
                v-if="transactionalConfigTab === 'custom' && messageStandard.transactional_rules"
                v-model="messageStandard.transactional_rules"
            />
        </animate-height>
    </vx-box>
</template>

<script setup lang="ts">
import AnimateHeight from 'vue-animate-height';
import { VxBox, VxAlert, VxTabs, VxTab } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faDiamondExclamation } from '@fortawesome/pro-solid-svg-icons';
import { ref, computed, watch } from 'vue';
import type { MessageStandardType } from '~/services/messageStandards/messageStandardsClient.types';
import MessageStandardRules from './MessageStandardRules.vue';
import MessageStandardRulesHeader from './MessageStandardRulesHeader.vue';
import { countMessageStandardRules } from './useMessageStandardDetails';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();
const props = withDefaults(
    defineProps<{
        showRulesInitially?: boolean;
    }>(),
    {
        showRulesInitially: false,
    }
);

const messageStandard = defineModel<MessageStandardType>({ required: true });
const marketingEnabled = defineModel<boolean>('marketingEnabled', { default: true });
const transactionalEnabled = defineModel<boolean>('transactionalEnabled', { default: true });

const showMarketing = ref(props.showRulesInitially);
const showTransactional = ref(props.showRulesInitially);

const marketingCount = computed(() => countMessageStandardRules(messageStandard.value.marketing_rules));

const transactionalConfigTab = computed({
    get: () => {
        if (messageStandard.value.transactional_config === null && !messageStandard.value.transactional_rules) {
            return undefined;
        }

        return messageStandard.value.transactional_config === null
            ? 'custom'
            : messageStandard.value.transactional_config;
    },
    set: (value: 'block_all_sending' | 'use_marketing_rules' | 'custom') => {
        messageStandard.value.transactional_config = value === 'custom' ? null : value;
    },
});

const transactionalCount = computed(() => {
    if (messageStandard.value.transactional_config === 'use_marketing_rules') {
        return countMessageStandardRules(messageStandard.value.marketing_rules);
    }

    if (transactionalConfigTab.value === 'block_all_sending') {
        return 1;
    }

    return countMessageStandardRules(messageStandard.value.transactional_rules);
});

watch(marketingCount, () => {
    if (marketingCount.value) {
        if (!marketingEnabled.value) {
            toasted.info('Marketing rules enabled');
        }
        marketingEnabled.value = true;
    }
});

watch([transactionalCount, transactionalConfigTab], () => {
    if (transactionalCount.value) {
        if (!transactionalEnabled.value) {
            toasted.info('Transactional rules enabled');
        }
        transactionalEnabled.value = true;
    }
});

defineExpose({
    marketingCount,
    transactionalCount,
});
</script>
